import { CompanyModule } from './company.types';

export const replaceIdWithPath = (path: string) => {
  return ('/' + path.split('/')[1]) as PageRoute;
};

export enum LandingSolutionPageRoute {
  EFFORTLESS_SCENARIO_MODELING = 'effortless-scenario-modeling',
  ENHANCED_INVESTOR_RELATIONS = 'enhanced-investor-relations',
  EQUITY_PLAN_AUTOMATION = 'equity-plan-automation',
  OPTIMIZED_FUNDRAISING_INSTRUMENTS = 'optimized-fundraising-instruments',
  SIMPLIFIED_CAP_TABLE = 'simplified-cap-table',
}

export enum TermsRoute {
  CONFIDENTIALITY = '/terms/confidentiality-notice',
  COOKIE_POLICY = '/terms/cookie-policy',
  DPA = '/terms/data-processing-agreement',
  PRIVACY_POLICY = '/terms/privacy-policy',
  TERMS_OF_SERVICE = '/terms/terms-of-service',
}

export enum PageRoute {
  ACCEPT_INVITATION = '/accept-invitation/:id',
  ALL_DONE = '/all-done',
  AUTH_GOOGLE_CALLBACK = '/auth/google/callback',
  BILLING = '/billing',
  CAP_TABLE = '/cap-table',
  COMPANY_SETTINGS = '/company-settings',
  COMPANY_SETUP = '/company-setup',
  DASHBOARD = '/dashboard',
  DATA_ROOM = '/data-room',
  DATA_ROOM_ACTIVITY = '/data-room/activity',
  DATA_ROOM_ACTIVITY_DATA_ROOMS = '/data-room/activity/data-rooms',
  DATA_ROOM_ACTIVITY_FILES = '/data-room/activity/files',
  DATA_ROOM_ACTIVITY_ROOM = '/data-room/activity/rooms/:dataRoomId',
  /**
   * Keyed by an opaque digest of the address, never by the address — a path segment reaches access
   * logs, Sentry breadcrumbs and the browser's own history, and `DATA_ROOM_VISITOR_KEY_LENGTH`
   * explains why a data room's audience does not belong in any of them. The key arrives on every
   * Activity row; nothing here derives it.
   */
  DATA_ROOM_ACTIVITY_VISITOR = '/data-room/activity/visitors/:visitorKey',
  DATA_ROOM_ACTIVITY_VISITORS = '/data-room/activity/visitors',
  DATA_ROOM_INVITE = '/data-room-invites/:token',
  DATA_ROOM_WORKSPACES = '/data-room/workspaces',
  DIVIDENDS = '/dividends',
  DOCUMENTS = '/documents',
  DOCUMENTS_SIGNATURES = '/signatures',
  DOCUMENTS_TEMPLATES = '/templates',
  ERROR = '*',
  EVENTS = '/events',
  FORGOT_PASSWORD = '/forgot-password',
  GUIDES = '/guides',
  HANG_TIGHT = '/hang-tight',
  HOME = '/',
  LIBRARY = '/library',
  LOGIN = '/login',
  MY_COMPANIES = '/my-companies',
  MY_HOLDINGS = '/holdings',
  ONBOARDING = '/onboarding',
  PASSWORD_RECOVERY = '/password-recovery/:id',
  PERKS = '/perks',
  POOL_PLANS = '/pool-plans',
  POOL_PLANS_PLAN = '/pool-plans/:poolId/:planId',
  POOL_PLANS_POOL = '/pool-plans/:poolId',
  PROFILE = '/profile',
  REGISTER = '/register',
  REGISTER_VERIFY = '/register/verify/:id',
  SAFES = '/safes',
  SCENARIO_DETAILS = '/scenario-modeling/:id',
  SCENARIO_MODELING = '/scenario-modeling',
  SETTINGS = '/settings',
  SHARE_CLASSES = '/share-classes',
  SHARED_CAP_TABLE = '/share/:companySlug/:linkId',
  STAKEHOLDERS = '/stakeholders',
  SUBSCRIPTION_OFFER_ENTERPRISE = '/enterprise-offer/:slug',
  SUBSCRIPTION_OFFER_GROWTH = '/growth-offer/:slug',
  SUBSCRIPTION_OFFER_STARTUP = '/startup-offer/:slug',
  VAULT = '/vault',
  WELCOME = '/welcome',
}

export const pageModule: { [key in PageRoute]?: CompanyModule } = {
  [PageRoute.CAP_TABLE]: CompanyModule.CAP_TABLE,
  [PageRoute.DIVIDENDS]: CompanyModule.DIVIDENDS,
  [PageRoute.EVENTS]: CompanyModule.EVENTS,
  [PageRoute.MY_HOLDINGS]: CompanyModule.MY_HOLDINGS,
  [PageRoute.POOL_PLANS]: CompanyModule.POOLS_AND_PLANS,
  [PageRoute.STAKEHOLDERS]: CompanyModule.STAKEHOLDER,
  [PageRoute.SHARE_CLASSES]: CompanyModule.SHARE_CLASSES,
  [PageRoute.VAULT]: CompanyModule.STAKEHOLDER_VAULT,
  [PageRoute.SAFES]: CompanyModule.SAFES,
  [PageRoute.DASHBOARD]: CompanyModule.DASHBOARD,
  [PageRoute.SCENARIO_MODELING]: CompanyModule.SCENARIO_MODELING,
  [PageRoute.SCENARIO_DETAILS]: CompanyModule.SCENARIO_MODELING,
};

export const pageTitle: Record<PageRoute, string> = {
  [PageRoute.HOME]: 'My Companies',
  [PageRoute.PROFILE]: 'My Profile',
  [PageRoute.MY_COMPANIES]: 'My Companies',
  [PageRoute.COMPANY_SETUP]: 'Company Setup',
  [PageRoute.COMPANY_SETTINGS]: 'Company Settings',
  [PageRoute.DASHBOARD]: 'Dashboard',
  [PageRoute.DATA_ROOM]: 'Data Room',
  [PageRoute.DATA_ROOM_ACTIVITY]: 'Data Room',
  [PageRoute.DATA_ROOM_ACTIVITY_DATA_ROOMS]: 'Data Room',
  [PageRoute.DATA_ROOM_ACTIVITY_FILES]: 'Data Room',
  [PageRoute.DATA_ROOM_ACTIVITY_ROOM]: 'Data Room',
  [PageRoute.DATA_ROOM_ACTIVITY_VISITOR]: 'Data Room',
  [PageRoute.DATA_ROOM_ACTIVITY_VISITORS]: 'Data Room',
  [PageRoute.DATA_ROOM_INVITE]: 'Data Room Invitation',
  [PageRoute.DATA_ROOM_WORKSPACES]: 'Data Room',
  [PageRoute.DIVIDENDS]: 'Dividends',
  [PageRoute.STAKEHOLDERS]: 'Stakeholders',
  [PageRoute.EVENTS]: 'Events',
  [PageRoute.MY_HOLDINGS]: 'My Holdings',
  [PageRoute.SAFES]: 'SAFEs',
  [PageRoute.FORGOT_PASSWORD]: 'Forgot Password',
  [PageRoute.SHARE_CLASSES]: 'Share Classes',
  [PageRoute.SHARED_CAP_TABLE]: 'Cap Table',
  [PageRoute.POOL_PLANS_POOL]: 'Pool Plans - Pool',
  [PageRoute.POOL_PLANS_PLAN]: 'Pool Plans - Plan',
  [PageRoute.POOL_PLANS]: 'Pool Plans',
  [PageRoute.CAP_TABLE]: 'Cap Table',
  [PageRoute.SETTINGS]: 'Settings',
  [PageRoute.ACCEPT_INVITATION]: 'Accept Invitation',
  [PageRoute.LOGIN]: 'Login',
  [PageRoute.REGISTER]: 'Register',
  [PageRoute.REGISTER_VERIFY]: 'Register Verification',
  [PageRoute.PASSWORD_RECOVERY]: 'Password Recovery',
  [PageRoute.BILLING]: 'Billing',
  [PageRoute.ERROR]: 'Error',
  [PageRoute.ALL_DONE]: 'All Done',
  [PageRoute.AUTH_GOOGLE_CALLBACK]: 'Signing in',
  [PageRoute.HANG_TIGHT]: 'Hang Tight',
  [PageRoute.ONBOARDING]: 'Onboarding',
  [PageRoute.GUIDES]: 'Guides',
  [PageRoute.VAULT]: 'The Vault',
  [PageRoute.SCENARIO_MODELING]: 'Scenario Modeling',
  [PageRoute.SCENARIO_DETAILS]: 'Scenario Modeling',
  [PageRoute.WELCOME]: 'Welcome',
  [PageRoute.PERKS]: 'Perks',
  [PageRoute.LIBRARY]: 'Library',
  [PageRoute.DOCUMENTS]: 'Documents',
  [PageRoute.DOCUMENTS_TEMPLATES]: 'Templates',
  [PageRoute.DOCUMENTS_SIGNATURES]: 'Signatures',
  [PageRoute.SUBSCRIPTION_OFFER_ENTERPRISE]: 'Subscription Offer',
  [PageRoute.SUBSCRIPTION_OFFER_GROWTH]: 'Subscription Offer',
  [PageRoute.SUBSCRIPTION_OFFER_STARTUP]: 'Subscription Offer',
};
